import type { ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath } from 'react-router';
import type { AxiosResponse } from 'axios';

import { DownloadState } from 'src/constants/downloadManager';
import type { ExportDownload, FrontendExport } from 'src/types/downloadManager';
import { getFileExtension } from 'src/helper/getFileExtension';
import trackEvent from 'src/services/analytics/trackEvent';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';

import useInterval from './hooks/useInterval';

const POLLING_INTERVAL = 5000;

export interface DownloadManagerContextValue {
  downloads: ExportDownload[];
  unavailableDownloads: ExportDownload[];
  showDownloadManager: boolean;
  downloadFile: (fileToDownload: ExportDownload) => void;
  hideExport: (id: string) => void;
  poll: () => void;
  hideAllDownloads: () => Promise<void>;
  onClickHandler?: () => void;
  addFrontendExport: (frontendExport: FrontendExport) => void;
}

const defaultContextValue: DownloadManagerContextValue = {
  downloads: [],
  unavailableDownloads: [],
  showDownloadManager: false,
  downloadFile: () => null,
  hideExport: () => null,
  poll: () => null,
  hideAllDownloads: () => Promise.resolve(),
  onClickHandler: () => null,
  addFrontendExport: () => null,
};

export const DownloadManagerContext =
  createContext<DownloadManagerContextValue>(defaultContextValue);

interface DownloadManagerProviderProps {
  children?: ReactNode;
}

export const DownloadManagerProvider = ({
  children,
}: DownloadManagerProviderProps) => {
  const [rawDownloads, setRawDownloads] = useState<ExportDownload[]>([]);
  // ref, used to track the latest value for the cleanup on unmount
  const rawDownloadsRef = useRef(rawDownloads);
  const [downloadQueue, setDownloadQueue] = useState<string[]>([]);
  const { user } = useUserContext();

  // Filter out downloads with hidden flag
  const downloads = useMemo(
    () =>
      rawDownloads.filter(
        (download) =>
          !download.hidden && download.status != DownloadState.ERROR,
      ),
    [rawDownloads],
  );

  const unavailableDownloads = useMemo(
    () =>
      rawDownloads.filter(
        (download) =>
          !download.hidden && download.status == DownloadState.ERROR,
      ),
    [rawDownloads],
  );

  // Checks if there is exports with status started
  const pollingActive = useMemo(
    () =>
      downloads.some((download) => download.status === DownloadState.PENDING),
    [downloads],
  );

  // Polling is only allowed if an export has started and no downloads are active
  const allowPolling = useMemo(
    () => pollingActive && !downloadQueue.length,
    [pollingActive, downloadQueue],
  );

  const poll = useCallback(() => {
    if (!user) return;
    HttpServiceInstance.get(apiPaths.EXPORT).then(
      (response: AxiosResponse<ExportDownload[]>) => {
        setRawDownloads(response.data);
      },
    );
  }, [user, pollingActive]);

  // we poll once on mount
  useEffect(() => {
    poll();
  }, [poll]);

  // Polling for new exports
  useInterval(
    () => {
      poll();
    },
    allowPolling ? POLLING_INTERVAL : null,
  );

  useEffect(() => {
    rawDownloadsRef.current = rawDownloads;
  }, [rawDownloads]);

  // Cleanup frontend export object URLs on unmount
  useEffect(
    () => () => {
      rawDownloadsRef.current.forEach((download) => {
        if (download.frontendGenerated && download.file) {
          URL.revokeObjectURL(download.file);
        }
      });
    },
    [],
  );

  // Show download manager if user is logged in and there are downloads
  const showDownloadManager = useMemo(
    () => user && (downloads.length > 0 || unavailableDownloads.length > 0),
    [downloads, user],
  );

  // Hide export from download manager
  const hideExport = useCallback(
    (id: string) => {
      // Hide them locally, so they disappear immediately
      setRawDownloads((prevState) =>
        prevState.map((download) =>
          download.id === id ? { ...download, hidden: true } : download,
        ),
      );

      // Find the download to be hidden
      const downloadToHide = rawDownloads.find((d) => d.id === id);

      // Only send request to server for backend-generated exports
      if (downloadToHide && !downloadToHide.frontendGenerated) {
        const endpoint = generatePath(apiPaths.HIDE_EXPORT, { id });
        HttpServiceInstance.post(endpoint, {});
      }

      // For frontend exports: revoke object URL to prevent memory leaks
      const frontendExport = rawDownloads.find(
        (d) => d.id === id && d.frontendGenerated,
      );
      if (frontendExport) {
        URL.revokeObjectURL(frontendExport.file);
      }
    },
    [rawDownloads],
  );

  const downloadFile = useCallback(
    (fileToDownload: ExportDownload) => {
      // Add to download queue to prevent polling
      setDownloadQueue((prevState) => [...prevState, fileToDownload.id]);
      // Set status to downloading
      setRawDownloads((prevState) =>
        prevState.map((download) =>
          download.id === fileToDownload.id
            ? { ...download, status: DownloadState.DOWNLOADING }
            : download,
        ),
      );

      // Create a download link with the correct filename
      const link = document.createElement('a');
      link.href = fileToDownload.file;
      link.download = fileToDownload.name; // Set the filename explicitly
      // The download attribute is ignored for cross-origin files, which are then navigated to.
      // Navigating in a new tab prevents PDFs from replacing the current page in Firefox.
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setDownloadQueue((prevState) =>
        prevState.filter((id) => id !== fileToDownload.id),
      );
      // timeout used to prevent a firefox bug which aborts pending requests after a download, compare e.g.
      // https://bugzilla.mozilla.org/show_bug.cgi?id=1683409
      // It also keeps object URLs of frontend exports alive until the download has started.
      setTimeout(() => {
        trackEvent('export_downloaded', {
          category: fileToDownload.category,
          language: fileToDownload.lang,
          file_format: getFileExtension(fileToDownload.name),
        });
        hideExport(fileToDownload.id);
      }, 200);
    },
    [hideExport],
  );

  const hideAllDownloads = useCallback(async () => {
    await Promise.all(downloads.map((dl) => hideExport(dl.id)));
  }, [downloads, hideExport]);

  const addFrontendExport = useCallback(
    ({ fileData, fileName, fileType, category = 'other' }: FrontendExport) => {
      const fileUrl = URL.createObjectURL(fileData);
      const id = `frontend-export-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const frontendExport: ExportDownload = {
        id,
        name: fileName,
        file: fileUrl,
        status: DownloadState.READY,
        hidden: false,
        category,
        frontendGenerated: true,
        fileType: fileType || 'application/octet-stream',
      };

      setRawDownloads((prevState) => [...prevState, frontendExport]);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      downloads,
      unavailableDownloads,
      showDownloadManager,
      downloadFile,
      hideExport,
      poll,
      hideAllDownloads,
      addFrontendExport,
    }),
    [
      downloads,
      unavailableDownloads,
      showDownloadManager,
      downloadFile,
      hideExport,
      poll,
      hideAllDownloads,
      addFrontendExport,
    ],
  );

  return (
    <DownloadManagerContext.Provider value={contextValue}>
      {children}
    </DownloadManagerContext.Provider>
  );
};

export const useDownloadManager = () => useContext(DownloadManagerContext);
